import { faPencil } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";
import { FALLBACK_ICON, ICON_MAP } from "./iconMap";
import type { TaskShortcut } from "@dashboard/api/task";

interface ShortcutTileProps {
    shortcut: TaskShortcut;
    editMode: boolean;
    onEditClick: (shortcut: TaskShortcut) => void;
    onDeleteClick: (shortcut: TaskShortcut) => void;
}

export function ShortcutTile({
    shortcut,
    editMode,
    onEditClick,
    onDeleteClick,
}: ShortcutTileProps) {
    const { t } = useTranslation();
    const icon = ICON_MAP[shortcut.icon] ?? FALLBACK_ICON;

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: shortcut.id,
        disabled: !editMode,
    });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.85 : 1,
        boxShadow: isDragging ? "0 4px 12px rgba(0, 0, 0, 0.15)" : undefined,
    };

    const tile = (
        <div
            ref={setNodeRef}
            style={style}
            className={`relative flex min-w-0 flex-col items-center gap-2 rounded-md border border-compendia-border bg-compendia-card px-2 pt-[14px] pb-3 text-center transition-colors ${
                editMode
                    ? "cursor-grab"
                    : "hover:border-compendia-foreground hover:bg-compendia-greige-10"
            }`}
            {...(editMode ? { ...attributes, ...listeners } : {})}
        >
            {editMode && (
                <>
                    <button
                        type="button"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onEditClick(shortcut);
                        }}
                        className="absolute top-1 left-1 z-10 flex size-[18px] items-center justify-center rounded-full border border-compendia-foreground bg-compendia-card text-[10px] text-compendia-foreground transition-colors hover:bg-compendia-greige-10"
                        aria-label={t("common.edit")}
                    >
                        <FontAwesomeIcon icon={faPencil} className="size-[10px]" />
                    </button>
                    <button
                        type="button"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onDeleteClick(shortcut);
                        }}
                        className="absolute top-1 right-1 z-10 flex size-[18px] items-center justify-center rounded-full bg-chameleon-pink text-[13px] leading-none font-bold text-white transition-opacity hover:opacity-90"
                        aria-label={t("common.delete")}
                    >
                        ×
                    </button>
                </>
            )}
            <FontAwesomeIcon icon={icon} className="size-5 text-compendia-muted" />
            <span className="w-full truncate text-[11px] font-medium text-compendia-foreground">
                {shortcut.title}
            </span>
        </div>
    );

    if (editMode) {
        return tile;
    }

    return (
        <a
            href={shortcut.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block no-underline"
        >
            {tile}
        </a>
    );
}
